/**
 * Users API
 * User management operations (admin)
 */

import apiClient from '@/lib/api-client'
import type {
  User,
  UserCreate,
  UserUpdate,
  UserPaginatedResponse,
  UserFilters,
  UserPasswordReset,
  Role,
  Office,
  OfficeCreate,
  Court,
  CourtCreate,
  LegalAssistanceType,
} from '@/types/user'
import type { PaginationParams } from '@/types/common'

export const usersApi = {
  /**
   * Get all users with filters (admin only)
   */
  async getAll(
    filters?: UserFilters,
    pagination?: PaginationParams
  ): Promise<UserPaginatedResponse> {
    const response = await apiClient.get<UserPaginatedResponse>('/users', {
      params: {
        ...filters,
        ...pagination,
      },
    })
    return response.data
  },

  /**
   * Get user by ID
   */
  async getById(id: string): Promise<User> {
    const response = await apiClient.get<User>(`/users/${id}`)
    return response.data
  },

  /**
   * Create new user (admin only)
   */
  async create(data: UserCreate): Promise<User> {
    const response = await apiClient.post<User>('/users', data)
    return response.data
  },

  /**
   * Update user
   */
  async update(id: string, data: UserUpdate): Promise<User> {
    const response = await apiClient.put<User>(`/users/${id}`, data)
    return response.data
  },

  /**
   * Delete user
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`)
  },

  /**
   * Reset user password (admin)
   */
  async resetPassword(id: string, data: UserPasswordReset): Promise<void> {
    await apiClient.put(`/users/${id}/password`, data)
  },

  /**
   * Get all available roles
   */
  async getRoles(): Promise<Role[]> {
    const response = await apiClient.get<Role[]>('/users/roles/all')
    return response.data
  },
}

export const officesApi = {
  /**
   * Get all offices
   */
  async getAll(): Promise<Office[]> {
    const response = await apiClient.get<Office[]>('/courts/offices')
    return response.data
  },

  /**
   * Get office by ID
   */
  async getById(id: number): Promise<Office> {
    const response = await apiClient.get<Office>(`/courts/offices/${id}`)
    return response.data
  },

  /**
   * Create new office
   */
  async create(data: OfficeCreate): Promise<Office> {
    const response = await apiClient.post<Office>('/courts/offices', data)
    return response.data
  },

  /**
   * Update office
   */
  async update(id: number, data: Partial<OfficeCreate>): Promise<Office> {
    const response = await apiClient.put<Office>(`/courts/offices/${id}`, data)
    return response.data
  },

  /**
   * Delete office
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/courts/offices/${id}`)
  },
}

export const courtsApi = {
  /**
   * Get all courts
   */
  async getAll(): Promise<Court[]> {
    const response = await apiClient.get<Court[]>('/courts/courts')
    return response.data
  },

  /**
   * Get court by ID
   */
  async getById(id: number): Promise<Court> {
    const response = await apiClient.get<Court>(`/courts/courts/${id}`)
    return response.data
  },

  /**
   * Create new court
   */
  async create(data: CourtCreate): Promise<Court> {
    const response = await apiClient.post<Court>('/courts/courts', data)
    return response.data
  },

  /**
   * Update court
   */
  async update(id: number, data: Partial<CourtCreate>): Promise<Court> {
    const response = await apiClient.put<Court>(`/courts/courts/${id}`, data)
    return response.data
  },

  /**
   * Delete court
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/courts/courts/${id}`)
  },
}

export const legalAssistanceTypesApi = {
  /**
   * Get all legal assistance types for visit filters
   * Returns types with IDs 300-499 (legal assistance and case types)
   */
  async getAll(): Promise<LegalAssistanceType[]> {
    const response = await apiClient.get<LegalAssistanceType[]>('/courts/legal-assistance-types')
    return response.data
  },
}

export default usersApi
